import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { Message } from '@graphql/generated';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';

type TeamReactivationMessageProps = {
  message: Message;
  filterVal?: string;
};

export const TeamReactivationMessage: React.FC<
  TeamReactivationMessageProps
> = ({ message }) => {
  const { t } = useTranslation('models');
  const { body, isSender, userRequest } = message;
  const isPending = userRequest?.status === 'pending';
  const isApproved = userRequest?.status === 'approved';

  const {
    setUpgradeConfirmModalOpen,
    setTeamValues,
    setIdTeamOpen,
    setUUIdTeamOpen,
  } = useWebDrawer();

  const { teams } = useListTeamsFromQuery({});
  const team = teams.find((tm) => tm.id === userRequest?.userRequestableId);

  const handleUpgradePress = () => {
    if (!team) return;
    setTeamValues(team);
    setIdTeamOpen(team.id ?? '');
    setUUIdTeamOpen(team.uuid ?? '');
    setUpgradeConfirmModalOpen(true);
  };

  return (
    <Box
      borderColor='grey02'
      borderWidth={1}
      borderRadius='s'
      backgroundColor='white'
      marginVertical='s'
      overflow='hidden'>
      {/* Header: light magenta background */}
      <Box
        flexDirection='row'
        alignItems='center'
        paddingHorizontal='xs'
        paddingVertical='xs'
        backgroundColor='magentaLightMild'>
        <Icon name='Users' variant='xs' marginRight='xxs' color='magentaDark' />
        <Text variant='metadata' color='magentaDark'>
          {t('models:chat.memberActivity')}
        </Text>
      </Box>

      {/* Content */}
      <Box padding='xs'>
        <Text
          variant='labelSmall'
          color={isApproved ? 'textPrimary' : 'textSecondary'}
          marginBottom='xxs'>
          {isApproved
            ? t('chat.teamPlanActivated')
            : t('chat.teamAccessPaused')}
        </Text>

        {!!body && (
          <Text
            variant='webBodySecondary'
            color='textSecondary'
            marginBottom={!isSender && isPending ? 'xs' : 'none'}
            style={{ letterSpacing: 0.28 }}>
            {body}
          </Text>
        )}

        {!isSender && isPending && !!team && (
          <Box flexDirection='row' alignItems='center'>
            <Button
              borderColor='black'
              backgroundColor='black'
              variant='create'
              isSmall
              centerIcon={false}
              onPress={handleUpgradePress}
              height={32}
              prefixMarginRight='xxs'
              prefix={<Icon name='Crown' variant='s' color='white' />}>
              {t('chat.upgradeToTeamPlan')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};
